import { apiSlice } from './apiSlice'
import type { Case } from './casesApi'

export interface MemberCardInfo {
  storage_path: string
  file_name: string
  mime_type: string
  uploaded_at: string
  uploaded_by: string
}

export interface TeamFileInfo {
  storage_path: string
  file_name: string
  mime_type: string
  uploaded_at: string
  uploaded_by: string
}

export interface TeamVideoInfo {
  storage_path: string
  file_name: string
  mime_type: string
  file_size?: string | number
  uploaded_at: string
  uploaded_by: string
}

export interface MemberDetails {
  student_id: string
  full_name: string
  date_of_birth: string
  university: string
  faculty: string
  email: string
  bio: string
  photo_url?: string
}

export interface TeamRegistrationIssue {
  key: string
  label: string
  message: string
  severity: 'info' | 'warning' | 'error' | string
  blocker?: boolean
}

export interface TeamRegistrationSummary {
  is_complete: boolean
  required_done: number
  required_total: number
  missing_count: number
  missing_keys: string[]
  has_profile_file: boolean
  has_intro_video: boolean
  issues: TeamRegistrationIssue[]
}

export interface Team {
  id: string
  name: string
  university: string
  members: string[]
  manager_id?: string
  status: string
  assigned_cases?: string[]
  created_at?: string
  updated_at?: string
  profile_text?: string | null
  profile_file?: TeamFileInfo | null
  intro_video?: TeamVideoInfo | null
  guest_visible?: boolean
  rejection_reason?: string | null
  reviewed_at?: string | null
  reviewed_by?: string | null
  member_cards?: Record<string, MemberCardInfo>
  member_details?: Record<string, MemberDetails>
  registration_summary?: TeamRegistrationSummary | null
  // ── Auto-approve (BR-01-04 ext) ──
  auto_approved_at?: string | null
  auto_approval_confidence?: number | null
  auto_review_outcome?: string | null
  auto_review_attempted_at?: string | null
  auto_review_reasons?: Array<{
    code: string
    message: string
    member_id?: string | null
    detail?: string | null
  }> | null
  // ── Tiến trình giải (set bởi reports_service.publish_round_results) ──
  // null/undefined = chưa publish vòng nào | "advanced_to_semifinal" = qua R1/R2
  // | "advanced_to_final" = qua R3 | "eliminated" = bị loại
  competition_status?: string | null
  competition_status_at?: string | null
  final_rank?: number | null
}

export interface PublicTeamMember {
  position: number
  role_label: string
  full_name: string
  university?: string | null
  faculty?: string | null
  bio?: string | null
  photo_url?: string | null
}

export interface PublicTeam {
  team_id: string
  name: string
  university: string
  status: string
  competition_status?: string | null
  final_rank?: number | null
  member_count: number
  profile_text?: string | null
  profile_file?: Pick<TeamFileInfo, 'file_name' | 'mime_type' | 'uploaded_at'> | null
  has_profile_file: boolean
  member_profiles?: PublicTeamMember[]
  reviewed_at?: string | null
}

export interface TeamCreate {
  name: string
  university: string
  members: string[]
  profile_text?: string
  profile_file?: TeamFileInfo | null
  intro_video?: TeamVideoInfo | null
  member_cards?: Record<string, MemberCardInfo>
  member_details?: Record<string, MemberDetails>
  /** P2-#18 (audit batch B): khớp BE `Team.guest_visible: bool = False`. */
  guest_visible?: boolean
}

export interface TeamReviewBody {
  decision: 'approve' | 'reject'
  reason?: string
}

export const teamsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTeams: builder.query<Team[], void>({
      query: () => '/teams/list',
      providesTags: ['Team'],
    }),

    getPublicTeams: builder.query<PublicTeam[], void>({
      query: () => '/public/teams',
      providesTags: ['Team'],
    }),

    getTeam: builder.query<Team, string>({
      query: (id) => `/teams/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Team', id }],
    }),

    createTeam: builder.mutation<Team, TeamCreate>({
      query: (body) => ({
        url: '/teams/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team'],
    }),

    updateTeam: builder.mutation<Team, { id: string } & Partial<TeamCreate>>({
      query: ({ id, ...data }) => ({
        url: `/teams/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { id }) => ['Team', { type: 'Team', id }],
    }),

    reviewTeam: builder.mutation<Team, { teamId: string } & TeamReviewBody>({
      query: ({ teamId, ...body }) => ({
        url: `/teams/${teamId}/review`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { teamId }) => ['Team', { type: 'Team', id: teamId }],
    }),

    deleteTeam: builder.mutation<{ message: string }, string>({
      query: (teamId) => ({
        url: `/teams/${teamId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, teamId) => ['Team', { type: 'Team', id: teamId }],
    }),

    uploadMemberCard: builder.mutation<MemberCardInfo, { memberId: string; file: File }>({
      query: ({ memberId, file }) => {
        const formData = new FormData()
        formData.append('member_id', memberId)
        formData.append('file', file)

        return {
          url: '/teams/member-card/upload',
          method: 'POST',
          body: formData,
        }
      },
    }),

    uploadTeamProfileFile: builder.mutation<TeamFileInfo, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData()
        formData.append('file', file)

        return {
          url: '/teams/profile-file/upload',
          method: 'POST',
          body: formData,
        }
      },
    }),

    uploadTeamIntroVideo: builder.mutation<TeamVideoInfo, { file: File }>({
      query: ({ file }) => {
        const formData = new FormData()
        formData.append('file', file)
        return {
          url: '/teams/intro-video/upload',
          method: 'POST',
          body: formData,
        }
      },
    }),

    downloadTeamProfile: builder.query<Blob, string>({
      query: (teamId) => ({
        url: `/teams/${teamId}/profile/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    // D.3 — stream/download video giới thiệu (chung 1 endpoint backend).
    streamTeamIntroVideo: builder.query<Blob, string>({
      query: (teamId) => ({
        url: `/teams/${teamId}/intro-video/stream`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    downloadPublicTeamProfile: builder.query<Blob, string>({
      query: (teamId) => ({
        url: `/public/teams/${teamId}/profile/download`,
        responseHandler: (response: Response) => response.blob(),
      }),
    }),

    getTeamCases: builder.query<Case[], string>({
      query: (teamId) => `/teams/${teamId}/cases`,
      providesTags: (_result, _error, teamId) => [{ type: 'Case', id: `team-${teamId}` }],
    }),

    // BR-01-04 ext — auto-approve details cho Admin UI.
    getTeamAutoReview: builder.query<
      {
        team_id: string
        status: string
        auto_review_outcome: string | null
        auto_review_attempted_at: string | null
        auto_review_reasons: Array<{
          code: string
          message: string
          member_id?: string | null
          detail?: string | null
        }>
        auto_approved_at: string | null
        auto_approval_confidence: number | null
        auto_approval_details: Record<string, unknown>
        feature_flag_enabled: boolean
        can_revoke: boolean
        revoke_blocked_reason: string | null
        reason_code_dictionary: Record<string, string>
      },
      string
    >({
      query: (teamId) => `/teams/${teamId}/auto-review`,
      providesTags: (_r, _e, teamId) => [{ type: 'Team', id: `auto-review-${teamId}` }],
    }),

    revokeAutoApproval: builder.mutation<Team, { teamId: string; reason: string }>({
      query: ({ teamId, reason }) => ({
        url: `/teams/${teamId}/revoke-auto-approval`,
        method: 'POST',
        body: { reason },
      }),
      invalidatesTags: (_result, _error, { teamId }) => [
        'Team',
        { type: 'Team', id: teamId },
        { type: 'Team', id: `auto-review-${teamId}` },
      ],
    }),
  }),
})

export const {
  useGetTeamsQuery,
  useGetPublicTeamsQuery,
  useGetTeamQuery,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useReviewTeamMutation,
  useDeleteTeamMutation,
  useUploadMemberCardMutation,
  useUploadTeamIntroVideoMutation,
  useUploadTeamProfileFileMutation,
  useLazyDownloadTeamProfileQuery,
  useLazyDownloadPublicTeamProfileQuery,
  useLazyStreamTeamIntroVideoQuery,
  useGetTeamCasesQuery,
  useGetTeamAutoReviewQuery,
  useRevokeAutoApprovalMutation,
} = teamsApi
